import { MERCHANT_OPERATIONS } from "@price-rescue/api-contracts";
import type { MerchantOperationQuery } from "@price-rescue/api-contracts";

import type { MerchantApiClient } from "../../types.js";
import type {
  AssignmentDraft,
  ConversationMessageDraft,
  CustomerNoteDraft,
  DecisionDraft,
  EscalateOverdueResult,
  GuestRequestCustomerCandidates,
  PriceMatchRequestDetail,
  RequestInternalNoteDraft,
  RequestAttachmentDownload,
  RequestAttachmentUpload,
  RequestQueueFilters,
  RequestQueuePage,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: {
    requestId: string;
  };
}

export interface MerchantRequestsApi {
  getCustomerLinkCandidates(requestId: string): Promise<GuestRequestCustomerCandidates>;
  linkGuestRequestCustomer(requestId: string, customerId: string): Promise<PriceMatchRequestDetail>;
  assignRequest(
    requestId: string,
    draft: AssignmentDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  createCustomerNote(
    requestId: string,
    draft: CustomerNoteDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  createInternalNote(
    requestId: string,
    draft: RequestInternalNoteDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  createConversationMessage(
    requestId: string,
    draft: ConversationMessageDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  createAttachmentUploadUrl(
    requestId: string,
    file: { name: string; type: string; size: number },
    idempotencyKey: string,
  ): Promise<RequestAttachmentUpload>;
  completeAttachmentUpload(
    requestId: string,
    attachmentId: string,
    file: { size: number },
  ): Promise<PriceMatchRequestDetail>;
  getAttachmentDownloadUrl(
    requestId: string,
    attachmentId: string,
  ): Promise<RequestAttachmentDownload>;
  removeAttachment(
    requestId: string,
    attachmentId: string,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  updateCustomerNote(
    requestId: string,
    noteId: string,
    draft: CustomerNoteDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  updateInternalNote(
    requestId: string,
    noteId: string,
    draft: RequestInternalNoteDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  escalateOverdueRequests(idempotencyKey: string): Promise<EscalateOverdueResult>;
  getRequest(requestId: string): Promise<PriceMatchRequestDetail>;
  listRequests(filters: RequestQueueFilters): Promise<RequestQueuePage>;
  retryEvidence(requestId: string, idempotencyKey: string): Promise<PriceMatchRequestDetail>;
  resendCustomerEmail(requestId: string, idempotencyKey: string): Promise<PriceMatchRequestDetail>;
  retryCouponPreparation(requestId: string): Promise<PriceMatchRequestDetail>;
  selectEvidence(
    requestId: string,
    snapshotId: string,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
  submitDecision(
    requestId: string,
    draft: DecisionDraft,
    idempotencyKey: string,
  ): Promise<PriceMatchRequestDetail>;
}

export function createMerchantRequestsApi(client: MerchantApiClient): MerchantRequestsApi {
  return {
    async getCustomerLinkCandidates(requestId) {
      const envelope = await client.operation<DataEnvelope<GuestRequestCustomerCandidates>>(
        MERCHANT_OPERATIONS.getMerchantRequestCustomerLinkCandidates,
        { pathParams: { requestId } },
      );
      return envelope.data;
    },
    async linkGuestRequestCustomer(requestId, customerId) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.linkMerchantRequestCustomer,
        { body: { customerId }, pathParams: { requestId } },
      );
      return envelope.data;
    },
    async assignRequest(requestId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.assignMerchantRequest,
        {
          body: {
            assigneeUserId: draft.assigneeUserId,
          },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async escalateOverdueRequests(idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<EscalateOverdueResult>>(
        MERCHANT_OPERATIONS.escalateOverdueRequests,
        {
          body: {},
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async createCustomerNote(requestId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.createMerchantRequestCustomerNote,
        {
          body: { body: draft.body },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async createInternalNote(requestId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.createMerchantRequestInternalNote,
        {
          body: { body: draft.body },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async createConversationMessage(requestId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.createMerchantRequestMessage,
        {
          body: { body: draft.body },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async createAttachmentUploadUrl(requestId, file, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<RequestAttachmentUpload>>(
        MERCHANT_OPERATIONS.createMerchantRequestAttachmentUploadUrl,
        {
          body: {
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async completeAttachmentUpload(requestId, attachmentId, file) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.completeMerchantRequestAttachmentUpload,
        {
          body: { sizeBytes: file.size },
          pathParams: { requestId, attachmentId },
        },
      );
      return envelope.data;
    },
    async getAttachmentDownloadUrl(requestId, attachmentId) {
      const envelope = await client.operation<DataEnvelope<RequestAttachmentDownload>>(
        MERCHANT_OPERATIONS.getMerchantRequestAttachmentDownloadUrl,
        {
          pathParams: { requestId, attachmentId },
        },
      );
      return envelope.data;
    },
    async removeAttachment(requestId, attachmentId, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.removeMerchantRequestAttachment,
        {
          pathParams: { requestId, attachmentId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async getRequest(requestId) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.getMerchantRequest,
        { pathParams: { requestId } },
      );
      return envelope.data;
    },
    async listRequests(filters) {
      return client.operation<RequestQueuePage>(MERCHANT_OPERATIONS.listMerchantRequests, {
        query: toOperationQuery(filters),
      });
    },
    async retryEvidence(requestId, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.retryMerchantRequestEvidence,
        {
          body: {},
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async resendCustomerEmail(requestId, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.resendMerchantRequestCustomerEmail,
        {
          body: {},
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async retryCouponPreparation(requestId) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.retryMerchantRequestCoupon,
        { body: {}, pathParams: { requestId } },
      );
      return envelope.data;
    },
    async selectEvidence(requestId, snapshotId, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.selectMerchantRequestEvidence,
        {
          body: { snapshotId },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async updateCustomerNote(requestId, noteId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.updateMerchantRequestCustomerNote,
        {
          body: { body: draft.body },
          pathParams: { requestId, noteId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async updateInternalNote(requestId, noteId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.updateMerchantRequestInternalNote,
        {
          body: { body: draft.body },
          pathParams: { requestId, noteId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
    async submitDecision(requestId, draft, idempotencyKey) {
      const envelope = await client.operation<DataEnvelope<PriceMatchRequestDetail>>(
        MERCHANT_OPERATIONS.decideMerchantRequest,
        {
          body: {
            action: draft.action,
            reasonCode: draft.reasonCode,
            reason: draft.reason,
            customerMessage: draft.customerMessage,
            internalNote: draft.internalNote,
            counterOfferPriceMinor: draft.counterOfferPriceMinor,
          },
          pathParams: { requestId },
          request: { idempotencyKey },
        },
      );
      return envelope.data;
    },
  };
}

function toOperationQuery(filters: RequestQueueFilters): MerchantOperationQuery {
  return filters as unknown as MerchantOperationQuery;
}
